import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, TrendingUp, TrendingDown, Minus } from "lucide-react";
import volleyballApi from "@/services/api";

export const StandingsTable = () => {
  const { championshipId } = useParams();
  const [standingsData, setStandingsData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchStandings = async () => {
      if (!championshipId) return;
      
      try {
        const data = await volleyballApi.getChampionshipStandings(championshipId);
        setStandingsData(data);
      } catch (error) {
        console.error("Error loading standings:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStandings();
  }, [championshipId]);
  
  const getPositionBadge = (position) => {
    if (position === 1) return { icon: Trophy, variant: "default", label: "1°" };
    if (position <= 4) return { icon: TrendingUp, variant: "secondary", label: position };
    if (position >= standingsData.length - 2) return { icon: TrendingDown, variant: "destructive", label: position };
    return { icon: Minus, variant: "outline", label: position };
  };
  
  if (loading) {
    return (
      <Card className="p-12 text-center">
        <p className="text-muted-foreground">Caricamento classifica...</p>
      </Card>
    );
  }
  
  return (
    <Card className="overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr className="border-b border-border">
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Pos</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Squadra</th>
              <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider">Punti</th>
              <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider">G.G.</th>
              <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider">V</th>
              <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider">P</th>
              <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider">Set V</th>
              <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider">Set P</th>
              <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider">Punti F</th>
              <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider">Punti S</th>
            </tr>
          </thead>
          <tbody>
            {standingsData.map((team, index) => {
              const badge = getPositionBadge(team.position);
              const Icon = badge.icon;
              
              return (
                <tr
                  key={index}
                  className="border-b border-border hover:bg-muted/30 transition-colors"
                >
                  <td className="px-6 py-4">
                    <Badge variant={badge.variant} className="min-w-[40px] justify-center">
                      {team.position === 1 ? <Icon className="w-4 h-4" /> : badge.label}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-semibold text-foreground">{team.team}</div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-xl font-bold text-primary">{team.points}</span>
                  </td>
                  <td className="px-6 py-4 text-center text-muted-foreground">{team.gamesPlayed}</td>
                  <td className="px-6 py-4 text-center text-success font-semibold">{team.wins}</td>
                  <td className="px-6 py-4 text-center text-destructive font-semibold">{team.losses}</td>
                  <td className="px-6 py-4 text-center text-muted-foreground">{team.setsWon}</td>
                  <td className="px-6 py-4 text-center text-muted-foreground">{team.setsLost}</td>
                  <td className="px-6 py-4 text-center text-muted-foreground">{team.pointsFor}</td>
                  <td className="px-6 py-4 text-center text-muted-foreground">{team.pointsAgainst}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-border">
        {standingsData.map((team, index) => {
          const badge = getPositionBadge(team.position);
          const Icon = badge.icon;
          
          return (
            <div key={index} className="p-4 hover:bg-muted/30 transition-colors">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Badge variant={badge.variant}>
                    {team.position === 1 ? <Icon className="w-4 h-4" /> : badge.label}
                  </Badge>
                  <div className="font-semibold text-foreground">{team.team}</div>
                </div>
                <span className="text-2xl font-bold text-primary">{team.points}</span>
              </div>
              
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="text-center">
                  <div className="text-muted-foreground text-xs mb-1">Partite</div>
                  <div className="font-semibold">{team.gamesPlayed}</div>
                </div>
                <div className="text-center">
                  <div className="text-muted-foreground text-xs mb-1">Vittorie</div>
                  <div className="font-semibold text-success">{team.wins}</div>
                </div>
                <div className="text-center">
                  <div className="text-muted-foreground text-xs mb-1">Sconfitte</div>
                  <div className="font-semibold text-destructive">{team.losses}</div>
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-2 mt-3 text-xs text-muted-foreground">
                <div className="text-center">
                  <div className="mb-1">Set V</div>
                  <div className="font-medium">{team.setsWon}</div>
                </div>
                <div className="text-center">
                  <div className="mb-1">Set P</div>
                  <div className="font-medium">{team.setsLost}</div>
                </div>
                <div className="text-center">
                  <div className="mb-1">Pt F</div>
                  <div className="font-medium">{team.pointsFor}</div>
                </div>
                <div className="text-center">
                  <div className="mb-1">Pt S</div>
                  <div className="font-medium">{team.pointsAgainst}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
